import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import { z } from 'zod';
import CustomFieldDefinitionDto from '@/features/dashboard/tourCustomFields/types/_CustomFieldDefinitionDto.ts';
import { DropdownCustomFieldDefinitionDto } from '@/features/dashboard/tourCustomFields/types/DropdownCustomFieldDefinitionDto.ts';
import { TextCustomFieldDefinitionDto } from '@/features/dashboard/tourCustomFields/types/TextCustomFieldDefinitionDto.ts';
import { LongTextCustomFieldDefinitionDto } from '@/features/dashboard/tourCustomFields/types/LongTextCustomFieldDefinitionDto.ts';

const fieldTypeToZodSchema = (
  field: CustomFieldDefinitionDto,
): z.ZodTypeAny => {
  switch (field.type) {
    case FieldType.TEXT:
    case FieldType.LONG_TEXT: {
      const textField = field as
        | TextCustomFieldDefinitionDto
        | LongTextCustomFieldDefinitionDto;
      const maxLength = textField.maxChars ?? 255;
      if (textField.required) {
        const minLength = textField.minChars ?? 1;
        return z
          .string({ error: 'Pole wymagane' })
          .trim()
          .min(minLength, `Pole musi mieć co najmniej ${minLength} znaków`)
          .max(maxLength, `Pole może mieć maksymalnie ${maxLength} znaków`);
      } else {
        const minLength = textField.minChars ?? 0;
        return z
          .string()
          .min(minLength, `Pole musi mieć co najmniej ${minLength} znaków`)
          .max(maxLength, `Pole może mieć maksymalnie ${maxLength} znaków`)
          .optional()
          .or(z.literal(''));
      }
    }
    case FieldType.CHECKBOX:
      if (field.required) {
        return z.boolean().refine((val) => val === true, {
          message: 'Pole musi być zaznaczone',
        });
      } else {
        return z.boolean();
      }
    case FieldType.DROPDOWN: {
      const dropdownField = field as DropdownCustomFieldDefinitionDto;
      const validIds = dropdownField.options
        .map((o) => o.id)
        .filter((id): id is string => id !== undefined);
      if (field.required) {
        return z
          .string({ error: 'Pole wymagane' })
          .min(1, 'Pole wymagane')
          .refine((val) => validIds.includes(val), 'Nieprawidłowa opcja');
      } else {
        return z
          .string()
          .nullish()
          .refine(
            (val) => !val || validIds.includes(val),
            'Nieprawidłowa opcja',
          );
      }
    }
    default:
      return z.string();
  }
};

export const buildCustomFieldsSchema = (fields: CustomFieldDefinitionDto[]) => {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const field of fields) {
    shape[field.id] = fieldTypeToZodSchema(field);
  }

  return z.object(shape);
};
